import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { API } from "@/lib/auth";
import { BRIDAL_STATUS } from "./bridalStatus";
import BridalEnquiryDetail from "./BridalEnquiryDetail";

const BOOKED = new Set(["confirmed", "trial_booked", "final_details", "balance_due", "paid_in_full", "wedding_complete"]);
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const buildEvents = (enquiries) => enquiries.flatMap((e) => {
  if (e.status === "declined") return [];
  const confirmed = BOOKED.has(e.status) && e.deposit_paid;
  const out = [];
  if (e.wedding_date) out.push({ id: `${e.id}-w`, eid: e.id, date: e.wedding_date, kind: "Wedding", name: e.full_name, confirmed, status: e.status });
  if (e.trial_date) out.push({ id: `${e.id}-t`, eid: e.id, date: e.trial_date, kind: "Trial", name: e.full_name, confirmed, status: e.status });
  return out;
});

const Chip = ({ ev, onOpen }) => (
  <button onClick={() => onOpen(ev.eid)} data-testid={`bridal-cal-event-${ev.id}`} title={`${ev.kind} · ${ev.name} · ${BRIDAL_STATUS[ev.status]?.l}`}
    className={`w-full text-left truncate rounded-md px-1.5 py-0.5 text-[10px] sm:text-[11px] leading-tight transition-opacity hover:opacity-80 ${
      ev.confirmed
        ? ev.kind === "Wedding" ? "bg-[#8B1B3A] text-white" : "bg-[#9C7F45] text-white"
        : "border border-dashed border-[hsl(var(--onyx)/0.45)] text-[hsl(var(--onyx)/0.75)] bg-white"
    }`}>
    {ev.kind === "Trial" ? "Trial · " : ""}{ev.name}
  </button>
);

function MonthGrid({ month, byDate, onOpen }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const today = iso(new Date());
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
  return (
    <div className="grid grid-cols-7 gap-px bg-[hsl(var(--onyx)/0.08)] rounded-2xl overflow-hidden border border-[hsl(var(--onyx)/0.08)]" data-testid="bridal-cal-grid">
      {WEEKDAYS.map((w) => <div key={w} className="bg-[hsl(var(--cream))] text-center text-[10px] uppercase tracking-wider py-2 text-[hsl(var(--onyx)/0.55)]">{w}</div>)}
      {cells.map((d, i) => {
        if (!d) return <div key={`x${i}`} className="bg-white/60 min-h-[64px] sm:min-h-[96px]" />;
        const k = iso(d);
        const evs = byDate[k] || [];
        return (
          <div key={k} data-testid={`bridal-cal-day-${k}`} className={`bg-white min-h-[64px] sm:min-h-[96px] p-1 sm:p-1.5 space-y-1 ${k === today ? "ring-2 ring-inset ring-[hsl(var(--gold))]" : ""}`}>
            <div className="text-[11px] text-[hsl(var(--onyx)/0.6)]">{d.getDate()}</div>
            {evs.slice(0, 3).map((ev) => <Chip key={ev.id} ev={ev} onOpen={onOpen} />)}
            {evs.length > 3 && <div className="text-[10px] text-[hsl(var(--onyx)/0.5)]">+{evs.length - 3} more</div>}
          </div>
        );
      })}
    </div>
  );
}

const Agenda = ({ events, onOpen }) => (
  <ul className="space-y-2" data-testid="bridal-cal-agenda">
    {events.length === 0 && <li className="text-sm text-[hsl(var(--onyx)/0.5)]">Nothing this month.</li>}
    {events.map((ev) => (
      <li key={ev.id}>
        <button onClick={() => onOpen(ev.eid)} data-testid={`bridal-cal-agenda-${ev.id}`} className="w-full text-left rounded-2xl bg-white border border-[hsl(var(--onyx)/0.06)] px-4 py-3 flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${ev.confirmed ? (ev.kind === "Wedding" ? "bg-[#8B1B3A]" : "bg-[#9C7F45]") : "border border-dashed border-[hsl(var(--onyx)/0.5)]"}`} />
          <span className="text-xs w-24 shrink-0 text-[hsl(var(--onyx)/0.6)]">{new Date(`${ev.date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>
          <span className="text-sm flex-1 truncate">{ev.kind} · {ev.name}</span>
          <span className="text-[10px] text-[hsl(var(--onyx)/0.5)] hidden sm:inline">{ev.confirmed ? BRIDAL_STATUS[ev.status]?.l : "Unconfirmed"}</span>
        </button>
      </li>
    ))}
  </ul>
);

export default function BridalCalendar() {
  const [enquiries, setEnquiries] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [openId, setOpenId] = useState(null);
  const load = () => axios.get(`${API}/admin/bridal/enquiries`).then((r) => setEnquiries(r.data.enquiries)).catch(() => toast.error("Could not load calendar"));
  useEffect(() => { load(); }, []);

  const events = useMemo(() => buildEvents(enquiries || []), [enquiries]);
  const prefix = `${month.getFullYear()}-${pad(month.getMonth() + 1)}`;
  const monthEvents = events.filter((e) => e.date.startsWith(prefix)).sort((a, b) => a.date.localeCompare(b.date));
  const byDate = monthEvents.reduce((acc, e) => ({ ...acc, [e.date]: [...(acc[e.date] || []), e] }), {});
  if (!enquiries) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const open = enquiries.find((e) => e.id === openId);
  const replace = (doc) => setEnquiries((list) => list.map((x) => (x.id === doc.id ? doc : x)));

  return (
    <div data-testid="bridal-calendar" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} aria-label="Previous month" data-testid="bridal-cal-prev" className="w-9 h-9 rounded-full border flex items-center justify-center bg-white"><ChevronLeft size={15} /></button>
          <div data-testid="bridal-cal-month" className="font-display text-2xl min-w-[170px] text-center">{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</div>
          <button onClick={() => shift(1)} aria-label="Next month" data-testid="bridal-cal-next" className="w-9 h-9 rounded-full border flex items-center justify-center bg-white"><ChevronRight size={15} /></button>
          <button onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="bridal-cal-today" className="text-xs px-3 py-1.5 rounded-full border bg-white">Today</button>
        </div>
        <div className="flex flex-wrap gap-3 text-[11px] text-[hsl(var(--onyx)/0.65)]">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#8B1B3A]" /> Confirmed wedding</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#9C7F45]" /> Confirmed trial</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-dashed border-[hsl(var(--onyx)/0.5)]" /> Unconfirmed enquiry</span>
        </div>
      </div>
      <MonthGrid month={month} byDate={byDate} onOpen={setOpenId} />
      <Agenda events={monthEvents} onOpen={setOpenId} />
      <BridalEnquiryDetail enquiry={open} onClose={() => setOpenId(null)} onChange={replace} onDeleted={() => { setOpenId(null); load(); }} />
    </div>
  );
}
